import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, User, AlertCircle, Check } from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ROLE_LABELS } from '@/types/user';

export function ProfilePage() {
  const { user } = useAuth();
  const { userProfile, loading, isPending, refreshProfile } = usePermissions();
  const [displayName, setDisplayName] = useState(userProfile?.display_name || '');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Update displayName when userProfile loads
  if (userProfile?.display_name && !displayName) {
    setDisplayName(userProfile.display_name);
  }

  const handleSave = async () => {
    if (!userProfile) return;

    setSaving(true);
    setSaveMessage(null);

    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ display_name: displayName })
        .eq('id', userProfile.id);

      if (error) throw error;

      setSaveMessage({ type: 'success', text: 'Profile updated successfully' });
      refreshProfile();
    } catch (err) {
      console.error('Failed to update profile:', err);
      setSaveMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold font-serif tracking-tight flex items-center gap-2">
          <User className="h-6 w-6" />
          Your Profile
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your account information
        </p>
      </div>

      {/* Pending User Banner */}
      {isPending && (
        <Card className="border-warning/30 bg-warning/10">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-warning-foreground">
                  Account Pending Approval
                </h3>
                <p className="text-sm text-warning-foreground mt-1">
                  Your account has been created, but you don't have access to any features yet.
                  An administrator will assign your role and permissions shortly.
                </p>
                <p className="text-sm text-warning-foreground mt-2">
                  In the meantime, you can update your profile information below.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>
            View and update your profile details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email (read-only) */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={user?.email || userProfile?.email || ''}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Your email address cannot be changed
            </p>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              type="text"
              placeholder="Enter your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This is how your name will appear to others
            </p>
          </div>

          {/* Role (read-only) */}
          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Input
              id="role"
              type="text"
              value={userProfile?.role ? ROLE_LABELS[userProfile.role] : 'Loading...'}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              Your role determines what features you can access
            </p>
          </div>

          {/* Save Message */}
          {saveMessage && (
            <div className={`flex items-center gap-2 text-sm ${
              saveMessage.type === 'success' ? 'text-success' : 'text-destructive'
            }`}>
              {saveMessage.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              {saveMessage.text}
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Details (read-only info) */}
      <Card>
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
          <CardDescription>
            Additional information about your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-muted-foreground">Account Status</dt>
              <dd className="text-sm font-medium capitalize">{userProfile?.status || 'Active'}</dd>
            </div>
            {userProfile?.last_login_at && (
              <div className="flex justify-between">
                <dt className="text-sm text-muted-foreground">Last Login</dt>
                <dd className="text-sm font-medium">
                  {new Date(userProfile.last_login_at).toLocaleString()}
                </dd>
              </div>
            )}
            {userProfile?.created_at && (
              <div className="flex justify-between">
                <dt className="text-sm text-muted-foreground">Account Created</dt>
                <dd className="text-sm font-medium">
                  {new Date(userProfile.created_at).toLocaleDateString()}
                </dd>
              </div>
            )}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
